import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND, NAV } from "../lib/site";
import { useSettings } from "../lib/SettingsContext";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const s = useSettings();
  const showAnnounce = s.announcement_enabled === "1" && s.announcement_text;
  const logoSrc = s.logo || BRAND.logo_mark;

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on();
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/75 backdrop-blur-2xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      {showAnnounce && (
        <div
          data-testid="announcement-bar"
          className="bg-[#d4af37] text-black text-center font-sans text-[11px] sm:text-xs tracking-[0.15em] uppercase py-2 px-4"
        >
          {s.announcement_text}
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-3 group shrink-0"
        >
          <img
            src={logoSrc}
            alt={BRAND.name}
            className="h-12 w-12 object-contain mix-blend-screen transition-transform duration-700 group-hover:rotate-[6deg]"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `link-underline font-sans text-[11px] tracking-[0.18em] uppercase font-medium transition-colors ${
                  isActive
                    ? "text-[#d4af37] active"
                    : "text-white/80 hover:text-white"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/admissions"
            data-testid="nav-cta-apply"
            className="hidden md:inline-flex btn-gold font-sans text-[10px] tracking-[0.24em] uppercase px-5 py-3 font-semibold whitespace-nowrap"
          >
            Apply Now
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* mobile */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${
          open ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 pb-8 pt-2 bg-black/95 border-t border-white/10">
          <ul className="flex flex-col gap-1">
            {NAV.map((n) => (
              <li key={n.to}>
                <NavLink
                  to={n.to}
                  data-testid={`mnav-link-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `block py-3 font-sans text-sm tracking-[0.22em] uppercase border-b border-white/5 ${
                      isActive ? "text-[#d4af37]" : "text-white/85"
                    }`
                  }
                  end={n.to === "/"}
                >
                  {n.label}
                </NavLink>
              </li>
            ))}
          </ul>
          <Link
            to="/admissions"
            data-testid="mnav-cta-apply"
            className="mt-6 inline-flex btn-gold font-sans text-[11px] tracking-[0.28em] uppercase px-6 py-3"
          >
            Apply Now
          </Link>
        </div>
      </div>
    </header>
  );
}
